import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CssBaseline,
  Link,
  Fade,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  Divider,
  Chip,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import {
  LockOutlined,
  PersonOutline,
  Visibility,
  VisibilityOff,
  ArrowForward,
  Security,
  Shield,
} from "@mui/icons-material";
import logo from "../../assets/spookfish_logo.png";
import { useAuth } from "../../context/AuthContext";
import {
  loginTheme as theme,
  GradientBackground,
  StyledPaper,
  LogoContainer,
  StyledLogo,
} from "../../theme/theme";

function Login() {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState({ show: false, message: "" });
  const [showLoader, setShowLoader] = useState(false);

  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();

  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    // Check for saved credentials
    const savedUsername = localStorage.getItem("remembered_username");
    if (savedUsername) {
      setCredentials(prev => ({ ...prev, username: savedUsername }));
      setRememberMe(true);
    }

    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async () => {
    // Validation
    if (!credentials.username.trim()) {
      setError({ show: true, message: "Username is required" });
      usernameRef.current.focus();
      return;
    }
    if (!credentials.password.trim()) {
      setError({ show: true, message: "Password is required" });
      passwordRef.current.focus();
      return;
    }

    setLoading(true);
    setError({ show: false, message: "" });
    
    try {
      await login(credentials);

      // Handle remember me
      if (rememberMe) {
        localStorage.setItem("remembered_username", credentials.username);
      } else {
        localStorage.removeItem("remembered_username");
      }
      
      // Show the loader animation
      setShowLoader(true);
      
      // Simulate loading time for the animation to play
      // You can replace this with actual data fetching if needed
      setTimeout(() => {
        navigate("/dashboard");
      }, 3000); // 3 seconds for the animation to complete 1 full cycle
      
    } catch (err) {
      setError({
        show: true,
        message: err.response?.status === 401 
          ? "Invalid username or password" 
          : "Login failed. Please try again."
      });
      setLoading(false);
    }
  };

  const handleKeyDown = (event, field) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (field === "username") {
        passwordRef.current.focus();
      } else if (field === "password") {
        handleLogin();
      }
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // If showing loader, render only the loader
  if (showLoader) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            background: 'radial-gradient(circle at center, #0E1626 0%, #060B14 100%)',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={56} thickness={3} sx={{ color: '#0C8599' }} />
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              letterSpacing: 3,
              textTransform: 'uppercase',
              fontSize: '0.85rem',
            }}
          >
            Loading...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GradientBackground>
        {/* Animated background particles */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            left: '15%',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.3)',
            animation: 'float 15s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '30%',
            right: '20%',
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.2)',
            animation: 'float 18s ease-in-out infinite reverse',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '60%',
            left: '10%',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.25)',
            animation: 'float 12s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '12%',
            right: '30%',
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            bgcolor: 'rgba(96,165,250,0.5)',
            animation: 'float 22s ease-in-out infinite reverse',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '12%',
            left: '35%',
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            bgcolor: 'rgba(211,47,47,0.15)',
            animation: 'float 16s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '40%',
            right: '8%',
            width: '5px',
            height: '5px',
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.35)',
            animation: 'float 20s ease-in-out infinite reverse',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
            pointerEvents: 'none',
          }}
        />

        <Container component="main" maxWidth="xs" sx={{ px: { xs: 2, sm: 3 } }}>
          <StyledPaper>
              <LogoContainer>
                <StyledLogo src={logo} alt="Spookfish Logo" />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography
                    component="h1"
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: '#060B14',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    Welcome Back
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    textAlign: 'center',
                    maxWidth: '280px',
                  }}
                >
                  Sign in to access your license management dashboard
                </Typography>
              </LogoContainer>

              {/* Error Alert */}
              <Fade in={error.show}>
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3, 
                    borderRadius: 2,
                    '& .MuiAlert-icon': { alignItems: 'center' }
                  }}
                  onClose={() => setError({ show: false, message: "" })}
                >
                  {error.message}
                </Alert>
              </Fade>

              <Box component="form" sx={{ width: "100%" }} noValidate>
                <TextField
                  fullWidth
                  label="Username"
                  margin="normal"
                  variant="outlined"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  onKeyDown={(e) => handleKeyDown(e, "username")}
                  inputRef={usernameRef}
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Enter your username"
                  error={error.show && !credentials.username}
                />
                
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  variant="outlined"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  onKeyDown={(e) => handleKeyDown(e, "password")}
                  inputRef={passwordRef}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Enter your password"
                  error={error.show && !credentials.password}
                />

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mt: 2,
                  mb: 3 
                }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        color="primary"
                        size="small"
                        sx={{
                          '&.Mui-checked': {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Remember me
                      </Typography>
                    }
                  />
                  <Link
                    href="#"
                    variant="body2"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      '&:hover': {
                        color: 'primary.dark',
                      },
                    }}
                  >
                    Forgot password?
                  </Link>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleLogin}
                  disabled={loading}
                  endIcon={!loading && <ArrowForward />}
                  sx={{
                    py: 1.8,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: '0%',
                      height: '0%',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      transform: 'translate(-50%, -50%)',
                      transition: 'width 0.6s, height 0.6s',
                    },
                    '&:hover::after': {
                      width: '300%',
                      height: '300%',
                    },
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                      <Typography component="span" sx={{ color: 'white' }}>
                        Signing in...
                      </Typography>
                    </Box>
                  ) : (
                    'Sign In'
                  )}
                </Button>

                <Divider sx={{ my: 4 }}>
                  <Typography variant="caption" sx={{ color: 'text.disabled', px: 1 }}>
                    SECURE LOGIN
                  </Typography>
                </Divider>

                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<LockOutlined sx={{ fontSize: '14px !important' }} />}
                      label="256-bit SSL"
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: '50px', fontSize: '0.7rem', color: 'text.disabled', borderColor: 'divider', height: 24 }}
                    />
                    <Chip
                      icon={<Shield sx={{ fontSize: '14px !important' }} />}
                      label="Encrypted"
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: '50px', fontSize: '0.7rem', color: 'text.disabled', borderColor: 'divider', height: 24 }}
                    />
                    <Chip
                      icon={<Security sx={{ fontSize: '14px !important' }} />}
                      label="Secure Login"
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: '50px', fontSize: '0.7rem', color: 'text.disabled', borderColor: 'divider', height: 24 }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                    © {new Date().getFullYear()} Spookfish Technologies. All rights reserved.
                  </Typography>
                </Box>
              </Box>
            </StyledPaper>
        </Container>
      </GradientBackground>
    </ThemeProvider>
  );
}

export default Login;